import React, { useState, useEffect } from "react";
import lawyerImg from "../assets/lawyer.png";
import clientImg from "../assets/client.png";
import { Link, useNavigate, useLocation } from "react-router-dom";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Determine dashboard route by role
  const getDashboardRoute = () => {
    if (!user) return "/";
    if (user.role === "client") return "/client/dashboard";
    if (user.role === "lawyer") return "/lawyer/dashboard";
    if (user.role === "admin") return "/admin/dashboard";
    return "/client/dashboard";
  };

  // Update user on mount, on custom event, and on route change
  useEffect(() => {
    const updateUser = () => {
      const token = localStorage.getItem("token");
      const userData = localStorage.getItem("user");
      //console.log("Updating user. Token:", token, "UserData:", userData);
      if (token && userData) {
        setUser(JSON.parse(userData));
      } else {
        setUser(null);
      }
    };
    updateUser();
    window.addEventListener("userChanged", updateUser);
    return () => window.removeEventListener("userChanged", updateUser);
  }, []);

  // Also update user when route changes (covers login/register navigation)
  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");
    if (token && userData) {
      setUser(JSON.parse(userData));
    } else {
      setUser(null);
    }
    setAccountOpen(false); // Close dropdown on route change
    setMenuOpen(false);    // Close mobile menu on route change
  }, [location]);

  // Logout handler
  const handleLogout = async () => {
    const token = localStorage.getItem("token");
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/logout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
    } catch (e) {
      console.error("Logout request failed:", e);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setAccountOpen(false);
    window.dispatchEvent(new Event("userChanged"));
    navigate("/login");
  };

  // Close dropdown and navigate
  const handleMenuLink = (to) => {
    setAccountOpen(false);
    setMenuOpen(false);
    navigate(to);
  };

  // --- CHANGES START HERE ---
  const dashboardRoute = getDashboardRoute();
  //console.log("dashboardRoute:", dashboardRoute);
  // --- CHANGES END HERE ---

  return (
    <header className="bg-gradient-to-r from-blue-800 to-blue-500 text-white shadow-lg">
      <div className="container mx-auto flex justify-between items-center px-4 py-3">
      <div className="flex items-center space-x-3">
          <Link to="/lawyers">
            <img
              src={lawyerImg}
              alt="Lawyer Connect"
              className="h-10 w-10 rounded-full border-2 border-white shadow-md hover:scale-110 transition-transform duration-200"
            />
          </Link>
          <Link to={user ? dashboardRoute : "/"} className="text-xl sm:text-2xl font-extrabold tracking-wide">
            {user ? "Dashboard" : "TheLawyerHelp"}
          </Link>
          <Link to="/clients">
            <img
              src={clientImg}
              alt="Client Connect"
              className="h-10 w-10 rounded-full border-2 border-white shadow-md hover:scale-110 transition-transform duration-200"
            />
          </Link>
        </div>
        {/* Desktop Nav */}
        <nav className="hidden md:flex space-x-6 items-center">
          <Link
            to={user ? dashboardRoute : "/"}
            className="hover:text-yellow-300 transition-colors duration-200"
            onClick={() => handleMenuLink(user ? dashboardRoute : "/")}
          >
            {user ? "Dashboard" : "Home"}
          </Link>
          <Link to="/lawyers" className="hover:text-yellow-300 transition-colors duration-200">Lawyers</Link>
          <Link to="/clients" className="hover:text-yellow-300 transition-colors duration-200">Clients</Link>
          <Link to="/contact" className="hover:text-yellow-300 transition-colors duration-200">Contact</Link>
          <div className="relative">
            <button
              onClick={() => setAccountOpen((open) => !open)}
              className="hover:text-yellow-300 transition-colors duration-200 flex items-center focus:outline-none"
            >
              {user ? user.name : "My Account"}
              <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {accountOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-white text-blue-900 rounded shadow-lg z-20">
                {user ? (
                  <>
                    <Link
                      to={dashboardRoute}
                      className="block px-4 py-2 font-semibold hover:bg-blue-100"
                      onClick={() => handleMenuLink(dashboardRoute)}
                    >
                      {user.name}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 hover:bg-blue-100"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="block px-4 py-2 hover:bg-blue-100"
                      onClick={() => handleMenuLink("/login")}
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      className="block px-4 py-2 hover:bg-blue-100"
                      onClick={() => handleMenuLink("/register")}
                    >
                      Registration
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        </nav>
        {/* Hamburger for mobile */}
        <button
          className="md:hidden flex flex-col space-y-1 focus:outline-none"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span className="w-6 h-0.5 bg-white"></span>
          <span className="w-6 h-0.5 bg-white"></span>
          <span className="w-6 h-0.5 bg-white"></span>
        </button>
      </div>
      {/* Mobile menu */}
      {menuOpen && (
        <nav className="md:hidden bg-blue-700 px-4 pb-4 flex flex-col space-y-2">
          <Link
            to={user ? dashboardRoute : "/"}
            className="hover:text-yellow-300 transition-colors duration-200"
            onClick={() => handleMenuLink(user ? dashboardRoute : "/")}
          >
            {user ? "Dashboard" : "Home"}
          </Link>
          <Link to="/lawyers" className="hover:text-yellow-300 transition-colors duration-200" onClick={() => handleMenuLink("/lawyers")}>Lawyers</Link>
          <Link to="/clients" className="hover:text-yellow-300 transition-colors duration-200" onClick={() => handleMenuLink("/clients")}>Clients</Link>
          <Link to="/contact" className="hover:text-yellow-300 transition-colors duration-200" onClick={() => handleMenuLink("/contact")}>Contact</Link>
          <div className="relative">
            <button
              onClick={() => setAccountOpen((open) => !open)}
              className="hover:text-yellow-300 transition-colors duration-200 flex items-center focus:outline-none w-full text-left"
            >
              {user ? user.name : "My Account"}
              <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {accountOpen && (
              <div className="mt-2 w-40 bg-white text-blue-900 rounded shadow-lg z-20">
                {user ? (
                  <>
                    {/* --- CHANGES: Username links to dashboard --- */}
                    <Link
                      to={dashboardRoute}
                      className="block px-4 py-2 font-semibold hover:bg-blue-100"
                      onClick={() => handleMenuLink(dashboardRoute)}
                    >
                      {user.name}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 hover:bg-blue-100"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="block px-4 py-2 hover:bg-blue-100"
                      onClick={() => handleMenuLink("/login")}
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      className="block px-4 py-2 hover:bg-blue-100"
                      onClick={() => handleMenuLink("/register")}
                    >
                      Registration
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        </nav>
      )}
    </header>
  );
}

